import { useEffect, useRef } from 'react';
import { useApi, useApiHolder, configApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import { platformToastApiRef } from '@platform/plugin-platform-react';

const POLL_INTERVAL = 30000; // 30s

interface ArgoState {
  name: string;
  sync: string;
  health: string;
}

/**
 * Surveille l'état ArgoCD des applications et publie un toast quand il change
 * (dégradé, restauré, désynchronisé). Extrait de `PlatformNotifications` : le
 * module qui appelle `/api/argocd` est celui qui le surveille. Ne rend rien.
 */
export const ArgocdHealthWatcher = () => {
  const configApi = useApi(configApiRef);
  const fetchApi = useApi(fetchApiRef);
  // Optionnelle : sans le socle, il n'y a personne à qui publier. Un `useApi`
  // lèverait et finirait en alerte de l'errorApi à chaque chargement.
  const toastApi = useApiHolder().get(platformToastApiRef);
  const backendUrl = configApi.getString('backend.baseUrl');

  const prevArgoRef = useRef<Map<string, ArgoState>>(new Map());
  const initializedRef = useRef(false);

  useEffect(() => {
    if (!toastApi) return undefined;

    const pollStatus = async () => {
      try {
        const argoRes = await fetchApi.fetch(`${backendUrl}/api/argocd/applications`);
        const argoApps: Array<{ name: string; syncStatus: string; healthStatus: string }> =
          argoRes.ok ? await argoRes.json() : [];
        const newArgo = new Map<string, ArgoState>();
        for (const app of argoApps) {
          newArgo.set(app.name, { name: app.name, sync: app.syncStatus, health: app.healthStatus });
        }

        if (initializedRef.current) {
          for (const [name, current] of newArgo) {
            const prev = prevArgoRef.current.get(name);
            if (prev) {
              if (prev.health !== 'Degraded' && current.health === 'Degraded') {
                toastApi.post({
                  type: 'error',
                  title: `Service dégradé`,
                  message: `${name} — le service est passé en état dégradé`,
                });
              } else if (prev.health === 'Degraded' && current.health === 'Healthy') {
                toastApi.post({
                  type: 'success',
                  title: `Service restauré`,
                  message: `${name} — le service est de nouveau opérationnel`,
                });
              } else if (prev.sync !== 'OutOfSync' && current.sync === 'OutOfSync') {
                toastApi.post({
                  type: 'warning',
                  title: `Désynchronisation`,
                  message: `${name} — le déploiement n'est plus synchronisé`,
                });
              }
            }
          }
        }
        prevArgoRef.current = newArgo;
        initializedRef.current = true;
      } catch (e) {
        // Silent fail — notifications are best-effort
      }
    };

    pollStatus();
    const interval = setInterval(pollStatus, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchApi, toastApi, backendUrl]);

  return null;
};
